import React, { useState } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Container from "@/components/ui/Container";
import { ArrowRight, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { articles } from "@/data/articles";
import { usePageTitle } from "@/hooks/usePageTitle";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formRateLimiter } from "@/lib/utils/rateLimit";
import { 
  validateSubscriberForm, 
  type SubscriberFormData, 
  type ValidationResult,
  type ValidationError,
  type ValidationFailure 
} from "@/lib/utils/formValidation";

const initialFormData: SubscriberFormData = {
  email: ""
};

const isValidationFailure = (result: ValidationResult): result is ValidationFailure => {
  return !result.success;
};

const NewsPage = () => {
  usePageTitle("Новости");
  const [activeCategory, setActiveCategory] = useState("Все новости");
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState<SubscriberFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [remainingTime, setRemainingTime] = useState(0);

  // Получаем уникальные категории и добавляем "Все новости"
  const uniqueCategories = ["Все новости",
    ...new Set(articles.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(article => article.category))];
  
  const news = articles;

  // Filter by category and search query
  const filteredNews = news
    .filter(item => 
      (activeCategory === "Все новости" || item.category === activeCategory) &&
      (searchQuery === "" || 
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
        item.excerpt.toLowerCase().includes(searchQuery.toLowerCase()))
    );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear validation error for the field being changed
    setValidationErrors(prev => prev.filter(error => error.field !== name));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check rate limiting
    if (!formRateLimiter.canSubmit("subscriber")) {
      const time = formRateLimiter.getRemainingTime("subscriber");
      setRemainingTime(time);
      toast.error(`Пожалуйста, подождите ${Math.ceil(time / 1000)} секунд перед повторной отправкой`);
      return;
    }

    // Validate form data
    const result = validateSubscriberForm(formData);
    if (isValidationFailure(result)) {
      setValidationErrors(result.errors);
      toast.error("Пожалуйста, исправьте ошибки в форме");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await api.subscribers.subscribe(result.data.email);
      
      // Send notification to Telegram with the current URL
      try {
        await api.subscribers.notifyTelegram(result.data.email);
      } catch (telegramError) {
        console.error("Error sending Telegram notification:", telegramError);
        // Don't show error to user, just log it
      }
      
      setIsSubmitted(true);
      setFormData(initialFormData);
      
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (error: any) {
      console.error("Error subscribing:", error);
      toast.error(error.message || "Произошла ошибка при оформлении подписки. Пожалуйста, попробуйте позже.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-secondary">
         
          <Container>
            <div className="max-w-3xl mx-auto text-center">
              <div className="tag mb-6 animate-fade-in">Новости и обновления</div>
              <h1 className="heading text-4xl md:text-5xl font-bold mb-6 animate-fade-in" style={{ animationDelay: "0.2s" }}>
                Последние <span className="text-gradient">новости</span> и события
              </h1>
              <p className="text-muted-foreground text-lg mb-8 animate-fade-in" style={{ animationDelay: "0.4s" }}>
                Будьте в курсе последних новостей, обновлений продуктов и полезных статей от нашей команды экспертов.
              </p>
              
              {/* Search */}
              <div className="relative max-w-xl mx-auto">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Поиск по новостям..."
                  className="w-full px-4 py-3 pl-10 bg-white border border-border rounded-full focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          </Container>
        </section>

        {/* News Section */}
        <section className="py-20">
          <Container>
            {/* Filter */}
            <div className="flex overflow-x-auto pb-4 mb-12 -mx-4 px-4 sm:justify-center sm:pb-0">
              <div className="flex space-x-2">
                {uniqueCategories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                      activeCategory === category
                        ? "bg-primary text-white"
                        : "bg-secondary text-foreground hover:bg-primary/10"
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* News Grid */}
            {filteredNews.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredNews.map((item) => (
                  <article 
                    key={item.id} 
                    className="relative group bg-white rounded-2xl overflow-hidden shadow-sm border border-border hover:border-primary/20 hover:shadow-md transition-all duration-300"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/0 z-10"></div>
                      <img 
                        src={item.image} 
                        alt={item.title}
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute bottom-4 left-4 z-20">
                        <div className="tag bg-white/80">{item.category}</div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm text-muted-foreground">{item.date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                      </div>
                      <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors duration-200">
                        {item.title}
                      </h3>
                      <p className="text-muted-foreground mb-5 text-sm">
                        {item.excerpt}
                      </p>
                      <Link 
                        to={`/news/${item.id}`} 
                        className="inline-flex items-center text-primary font-medium"
                      >
                        Читать далее
                        <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform duration-200" />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <h3 className="text-xl font-semibold mb-2">Ничего не найдено</h3>
                <p className="text-muted-foreground mb-6">По вашему запросу не найдено новостей. Попробуйте изменить параметры поиска.</p>
                <button 
                  onClick={() => {setSearchQuery(""); setActiveCategory("Все новости");}}
                  className="button-secondary"
                >
                  Сбросить фильтры
                </button>
              </div>
            )}
          </Container>
        </section>

        {/* Subscribe Section */}
        <section className="py-20 bg-secondary">
          <Container>
            <div className="glass rounded-2xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="heading text-3xl font-bold mb-4">
                    Подпишитесь на нашу рассылку
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Получайте последние новости, обновления продуктов и полезные статьи прямо на вашу почту. Никакого спама, только самая важная информация.
                  </p>
                  
                  {isSubmitted ? (
                    <div className="text-center py-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold mb-2">Подписка оформлена!</h3>
                      <p className="text-muted-foreground">
                        Спасибо за подписку. Теперь вы будете получать наши новости.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-2">
                      <div className="flex-1">
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Ваш email"
                          className={`w-full px-4 py-2 bg-white border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                            validationErrors.some(e => e.field === "email") ? "border-red-500" : "border-border"
                          }`}
                        />
                        {validationErrors.some(e => e.field === "email") && (
                          <p className="mt-1 text-sm text-red-500">
                            {validationErrors.find(e => e.field === "email")?.message}
                          </p>
                        )}
                      </div>
                      <button 
                        type="submit" 
                        disabled={isSubmitting || remainingTime > 0}
                        className="button-primary whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Отправка...
                          </span>
                        ) : remainingTime > 0 ? (
                          <span className="flex items-center">
                            Подождите {Math.ceil(remainingTime / 1000)} сек
                          </span>
                        ) : (
                          <span className="flex items-center">
                            Подписаться
                          </span>
                        )}
                      </button>
                    </form>
                  )}
                </div>
                <div className="relative">
                  <div className="h-auto bg-white/5 rounded-2xl overflow-hidden p-6">
                    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1591017403286-fd8493524e1e?q=80&w=2070')] bg-cover bg-center opacity-10 rounded-2xl"></div>
                    <div className="relative h-full flex flex-col items-center justify-center text-center space-y-4">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                          <rect width="16" height="13" x="4" y="5" rx="2"></rect>
                          <path d="m22 5-8 5-8-5"></path>
                          <path d="M20 18V7"></path>
                          <path d="M4 7v11"></path>
                        </svg>
                      </div>
                      <h3 className="text-2xl font-semibold">Будьте в курсе</h3>
                      <p className="text-muted-foreground">
                        Подписывайтесь на нашу рассылку, чтобы первыми узнавать о новых разработках, обновлениях продуктов и отраслевых тенденциях.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default NewsPage;
